import React, { useCallback, useState } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Main } from '@expo/html-elements';
import { theme, useResponsive } from '@sg/garnish';

import {
  CheckoutLayout,
  CheckoutLayoutColumn,
  PageWrapper,
} from '@order/components';
import type { SubscriptionCreditCard } from '@order/graphql';
import { useTelemetry } from '@order/Telemetry';

import {
  SweetpassCheckoutBlock,
  SweetpassCheckoutCardBlock,
  SweetpassCheckoutFooter,
  SweetpassCheckoutHeader,
} from './components';

// ────────────────────────────────────────────────────────────────────────────────

export const SweetpassCheckoutScreenContent = (
  props: SweetpassCheckoutScreenContentProps,
) => {
  const {
    testID,
    termsAndConditions,
    selectedCreditCard,
    creditCards,
    handleSelectCreditCard,
    ctaHandler,
  } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const navigation = useNavigation();
  const { track } = useTelemetry();
  const { match, currentBreakpoint } = useResponsive();

  // ─── State ───────────────────────────────────────────────────────────

  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const hasCreditCards = (creditCards?.length ?? 0) > 0;
  const canCheckout = hasAcceptedTerms && hasCreditCards;

  // ─── Callbacks ───────────────────────────────────────────────────────

  const handleCta = useCallback(async () => {
    setIsLoading(true);
    await ctaHandler();
    setIsLoading(false);
  }, [ctaHandler]);

  const handleAddCreditCard = useCallback(() => {
    track('sweetpass_account.add_payment_cta.tapped');
    navigation.navigate('Modal', {
      screen: 'SweetpassPaymentMethodAdd',
    });
  }, [navigation, track]);

  const handleAcceptTerms = useCallback(
    (toggle: boolean) => {
      setHasAcceptedTerms(toggle);

      if (toggle) track('sweetpass_account.terms.viewed');
    },
    [track],
  );

  // ─── Styling ─────────────────────────────────────────────────────────

  const responsiveStyle = match([
    styles.wrapperMobile,
    styles.wrapperTablet,
    styles.wrapperDesktop,
  ]);
  const style = [styles.wrapper, responsiveStyle];

  const containerResponsiveStyle = match([
    {},
    styles.containerTabletUp,
    styles.containerDesktopUp,
  ]);
  const containerStyles = [styles.container, containerResponsiveStyle];

  return (
    <>
      <PageWrapper
        withHeaderTitle
        withoutFooter
        contentContainerStyle={style}
        testID="sg-sweetpass-checkout-wrapper"
      >
        <Main testID={testID} style={containerStyles}>
          <SweetpassCheckoutHeader />

          <CheckoutLayout>
            <CheckoutLayoutColumn>
              <SweetpassCheckoutCardBlock
                selectedCreditCard={selectedCreditCard}
                creditCards={creditCards}
                handleAddCreditCard={handleAddCreditCard}
                handleSelectCreditCard={handleSelectCreditCard}
              />
            </CheckoutLayoutColumn>

            <CheckoutLayoutColumn>
              <SweetpassCheckoutBlock
                termsAndConditions={termsAndConditions}
                hasAcceptedTerms={hasAcceptedTerms}
                canCheckout={canCheckout}
                isLoading={isLoading}
                handleCta={handleCta}
                handleAcceptTerms={handleAcceptTerms}
              />
            </CheckoutLayoutColumn>
          </CheckoutLayout>
        </Main>
      </PageWrapper>

      {currentBreakpoint.isXS ? (
        <SweetpassCheckoutFooter
          canCheckout={canCheckout}
          isLoading={isLoading}
          handleCta={handleCta}
        />
      ) : null}
    </>
  );
};

// ─── Types ─────────────────────────────────────────────────────────────

type SweetpassCheckoutScreenContentProps = {
  readonly termsAndConditions?: string;
  readonly selectedCreditCard?: Partial<SubscriptionCreditCard>;
  readonly creditCards: ReadonlyArray<Partial<SubscriptionCreditCard>>;
  readonly ctaHandler: (() => Promise<void>) | (() => void);
  readonly handleSelectCreditCard: (
    creditCard: Partial<SubscriptionCreditCard>,
  ) => void;
} & Pick<ViewProps, 'testID'>;

// ─── Styles ────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    flexGrow: 1,
    backgroundColor: theme.colors.GREEN_2,
  },
  wrapperMobile: {
    backgroundColor: theme.colors.OATMEAL,
  },
  wrapperTablet: {
    paddingTop: theme.spacing['6'],
  },
  wrapperDesktop: {
    paddingTop: theme.spacing['10'],
  },
  container: {
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['6'],
  },
  containerTabletUp: {
    maxWidth: 500,
  },
  containerDesktopUp: {
    maxWidth: 1024,
  },
});
